/**
 * What requests mean: the kinds of request, the categories of message and each
 * channel's words for them, and the business types and groups a request may
 * name. Data, never code — a send asks these at the moment it goes.
 *
 * Read with `view`, changed with `manage`, every change audited. A kind's
 * change applies to requests made after it: one already in force keeps what it
 * meant on the day it applied.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Users } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Confirm,
  Input,
  Modal,
  QueryPending,
  ReadOnly,
  RecordSelect,
  Sections,
  Select,
  Switch,
  toast,
  useCan,
} from '@connect/web-shared';

import {
  CHANNEL_LABEL,
  requestKeys,
  requestsApi,
  type Classification,
  type Named,
  type RequestType,
  type Settings,
} from './requests-api';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);
const MANAGE = 'messaging.individual_request.manage';

export function RequestSettings() {
  const settings = useQuery({ queryKey: requestKeys.settings, queryFn: requestsApi.settings });
  if (!settings.data) return <QueryPending query={settings} label="Loading the settings" />;
  return (
    <Sections
      label="Settings"
      items={[
        {
          id: 'kinds',
          label: 'Kinds of request',
          hint: 'What each one stops, and how it is handled',
          content: <Kinds settings={settings.data} />,
        },
        {
          id: 'categories',
          label: 'Categories',
          hint: 'The kinds of message a person can say no to',
          content: <Categories settings={settings.data} />,
        },
        {
          id: 'words',
          label: 'Channel words',
          hint: 'Each channel’s own word, and its category',
          content: <Words settings={settings.data} />,
        },
        {
          id: 'groups',
          label: 'Business types & groups',
          hint: 'What a request’s scope may name',
          content: <Groups />,
        },
      ]}
    />
  );
}

function useSettingsSaved() {
  const queryClient = useQueryClient();
  return async (data?: Settings) => {
    if (data) queryClient.setQueryData(requestKeys.settings, data);
    await queryClient.invalidateQueries({ queryKey: requestKeys.all });
  };
}

// ── kinds of request ────────────────────────────────────────
const FLAGS: { key: keyof RequestType; label: string; hint: string; erasureOnly?: boolean }[] = [
  { key: 'active', label: 'Offered', hint: 'Staff may record it. A person’s STOP is always taken.' },
  { key: 'auto_apply', label: 'Applies itself', hint: 'From the person, proved and for every business, it applies at once.' },
  { key: 'requires_verification', label: 'Needs verifying', hint: 'Staff record how they checked it was the person.' },
  { key: 'spares_requested', label: 'Spares what they ask for', hint: 'A code they request, an answer to their own message, still go.' },
  { key: 'notify_clients', label: 'Tells the businesses', hint: 'Each business that keeps them is emailed when it applies.' },
  { key: 'blocks_readd', label: 'Keeps them out', hint: 'A business whose records were erased cannot add them back.', erasureOnly: true },
];

function Kinds({ settings }: { settings: Settings }) {
  const manage = useCan(MANAGE);
  const saved = useSettingsSaved();
  const save = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<RequestType> }) => requestsApi.updateType(id, body),
    onSuccess: async (data) => {
      toast.success('Saved', 'Requests made from now on follow it.');
      await saved(data);
    },
    onError: (error) => toast.error('Not saved', errorText(error)),
  });
  const label = (key: string) => settings.categories.find((c) => c.key === key)?.label ?? key;
  return (
    <ReadOnly when={!manage}>
      <div className="space-y-3">
        {settings.types.map((kind) => (
          <Card key={kind.public_id}>
            <CardHeader
              title={
                <span className="flex flex-wrap items-center gap-2">
                  {kind.label}
                  {kind.erasure ? <Badge tone="danger">Erasure</Badge> : null}
                  {kind.effect === 'allow' ? <Badge tone="success">Exception</Badge> : null}
                </span>
              }
              description={kind.description}
            />
            <CardBody className="space-y-3">
              <p className="text-xs text-muted">
                Covers:{' '}
                {kind.categories.length ? kind.categories.map(label).join(', ') : 'every kind of message'}
                {kind.needs_clients ? ' · names the businesses it is about' : ''}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {FLAGS.filter((f) => !f.erasureOnly || kind.erasure).map((flag) => (
                  <Switch
                    key={flag.key}
                    label={flag.label}
                    hint={flag.hint}
                    checked={Boolean(kind[flag.key])}
                    disabled={save.isPending}
                    onChange={(event) => save.mutate({ id: kind.public_id, body: { [flag.key]: event.target.checked } })}
                  />
                ))}
              </div>
              {kind.effect === 'block' && (kind.erasure || !kind.auto_apply) ? (
                <DecisionSettings kind={kind} saving={save.isPending} save={(body) => save.mutate({ id: kind.public_id, body })} />
              ) : null}
            </CardBody>
          </Card>
        ))}
      </div>
    </ReadOnly>
  );
}

/**
 * How long a business has to answer a request it decides — an erasure, or a
 * request that is not a standard stop — and, for an erasure, how long its
 * sending records are kept once it said delete. The business decides about its
 * own customers; Connect routes, reminds and carries out its answer.
 */
function DecisionSettings({
  kind,
  saving,
  save,
}: {
  kind: RequestType;
  saving: boolean;
  save: (body: Partial<RequestType>) => void;
}) {
  const [days, setDays] = useState(String(kind.client_response_days));
  const [keep, setKeep] = useState(String(kind.history_retention_days));
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Input
        label="Days a business has to answer"
        type="number"
        min={7}
        max={60}
        value={days}
        disabled={saving}
        hint="At least 7. Reminded a week and a day before; then it needs action here. The person is owed an answer within 30 days."
        onChange={(event) => setDays(event.target.value)}
        onBlur={() => Number(days) !== kind.client_response_days && save({ client_response_days: Number(days) })}
      />
      {kind.erasure ? (
        <Input
          label="Days sending records are kept"
          type="number"
          min={365}
          max={3650}
          value={keep}
          disabled={saving}
          hint="After a business said delete: then the person’s number, address and messages are stripped from them. At least a year: the law requires the log."
          onChange={(event) => setKeep(event.target.value)}
          onBlur={() => Number(keep) !== kind.history_retention_days && save({ history_retention_days: Number(keep) })}
        />
      ) : null}
    </div>
  );
}

// ── categories ──────────────────────────────────────────────
function Categories({ settings }: { settings: Settings }) {
  const manage = useCan(MANAGE);
  const saved = useSettingsSaved();
  const [adding, setAdding] = useState(false);
  const toggle = useMutation({
    mutationFn: ({ id, label, description, restrictable }: { id: string; label: string; description: string; restrictable: boolean }) =>
      requestsApi.updateCategory(id, { label, description, restrictable }),
    onSuccess: saved,
    onError: (error) => toast.error('Not saved', errorText(error)),
  });
  return (
    <Card>
      <CardHeader
        title="Categories"
        description="A person stops categories, whatever channel a message comes on. A category nobody may stop is an exception, written down here to exist."
        actions={
          manage ? (
            <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
              <Plus className="size-4" />
              Add
            </Button>
          ) : null
        }
      />
      <CardBody>
        <ReadOnly when={!manage}>
          <ul className="divide-y divide-line">
            {settings.categories.map((category) => (
              <li key={category.public_id} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-heading">{category.label}</p>
                  <p className="text-xs text-muted">{category.description}</p>
                </div>
                <Switch
                  label="A person may stop it"
                  checked={category.restrictable}
                  disabled={toggle.isPending}
                  onChange={(event) =>
                    toggle.mutate({
                      id: category.public_id,
                      label: category.label,
                      description: category.description,
                      restrictable: event.target.checked,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </ReadOnly>
      </CardBody>
      <AddCategory open={adding} onClose={() => setAdding(false)} />
    </Card>
  );
}

function AddCategory({ open, onClose }: { open: boolean; onClose: () => void }) {
  const saved = useSettingsSaved();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () => requestsApi.createCategory({ key, label, description, restrictable: true }),
    onSuccess: async (data) => {
      toast.success('Category added', 'Map channel words to it so sends are counted in it.');
      onClose();
      await saved(data);
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a category"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} disabled={!key || !label.trim()} onClick={() => create.mutate()}>
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {create.isError ? <Alert tone="danger">{errorText(create.error) ?? 'Not added.'}</Alert> : null}
        <Input label="Key" hint="Lower-case letters, digits and _." value={key} onChange={(e) => setKey(e.target.value)} />
        <Input label="Name" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Input label="What it is" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
    </Modal>
  );
}

// ── channel words ───────────────────────────────────────────
function Words({ settings }: { settings: Settings }) {
  const manage = useCan(MANAGE);
  const saved = useSettingsSaved();
  const [channel, setChannel] = useState('sms');
  const [word, setWord] = useState('');
  const [category, setCategory] = useState(settings.categories[0]?.key ?? '');
  const set = useMutation({
    mutationFn: (body: { channel: string; word: string; category: string }) => requestsApi.setMapping(body),
    onSuccess: async (data) => {
      setWord('');
      await saved(data);
    },
    onError: (error) => toast.error('Not saved', errorText(error)),
  });
  const remove = useMutation({
    mutationFn: (id: number) => requestsApi.deleteMapping(id),
    onSuccess: saved,
    onError: (error) => toast.error('Not removed', errorText(error)),
  });
  const label = (key: string) => settings.categories.find((c) => c.key === key)?.label ?? key;
  const channels = ['*', ...settings.channels];
  return (
    <Card>
      <CardHeader
        title="Channel words"
        description="An SMS template is “Promotional”, a WhatsApp one “marketing”. A word with no category here counts as promotional — the most restricted — so a new one never slips past an opt-out."
      />
      <CardBody className="space-y-3">
        {manage ? (
          <form
            className="grid items-end gap-2 sm:grid-cols-[10rem_1fr_12rem_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              set.mutate({ channel, word, category });
            }}
          >
            <Select
              label="Channel"
              value={channel}
              options={channels.map((c) => ({ value: c, label: CHANNEL_LABEL[c as keyof typeof CHANNEL_LABEL] ?? c }))}
              onChange={(e) => setChannel(e.target.value)}
            />
            <Input label="Its word" value={word} onChange={(e) => setWord(e.target.value)} />
            <Select
              label="Category"
              value={category}
              options={settings.categories.map((c) => ({ value: c.key, label: c.label }))}
              onChange={(e) => setCategory(e.target.value)}
            />
            <Button type="submit" variant="secondary" loading={set.isPending} disabled={!word.trim()}>
              Save
            </Button>
          </form>
        ) : null}
        <ul className="divide-y divide-line text-sm">
          {settings.mappings.map((mapping) => (
            <li key={mapping.id} className="flex items-center gap-3 py-2">
              <span className="w-28 shrink-0 text-muted">{CHANNEL_LABEL[mapping.channel] ?? mapping.channel}</span>
              <span className="min-w-0 flex-1 font-medium text-heading">
                {mapping.word === '*' ? 'Any other word' : mapping.word}
              </span>
              <Badge>{label(mapping.category)}</Badge>
              {manage ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${mapping.word}`}
                  onClick={() => remove.mutate(mapping.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

// ── business types and groups ───────────────────────────────
function Groups() {
  const manage = useCan(MANAGE);
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: requestKeys.classifications, queryFn: requestsApi.classifications });
  const [editing, setEditing] = useState<Classification | 'new' | null>(null);
  const [members, setMembers] = useState<Classification | null>(null);
  const [deleting, setDeleting] = useState<Classification | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => requestsApi.deleteClassification(id),
    onSuccess: async () => {
      toast.success('Deleted');
      await queryClient.invalidateQueries({ queryKey: requestKeys.classifications });
    },
    onError: (error) => toast.error('Not deleted', errorText(error)),
  });
  return (
    <Card>
      <CardHeader
        title="Business types & groups"
        description="A request may cover, leave out or let through a business type or a group. Who is in one decides what such a request covers — from the moment they join."
        actions={
          manage ? (
            <Button size="sm" variant="secondary" onClick={() => setEditing('new')}>
              <Plus className="size-4" />
              Add
            </Button>
          ) : null
        }
      />
      <CardBody>
        {!groups.data ? (
          <QueryPending query={groups} label="Loading" />
        ) : groups.data.length === 0 ? (
          <p className="text-sm text-muted">None yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {groups.data.map((group) => (
              <li key={group.public_id} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-heading">
                    {group.name}{' '}
                    <span className="text-xs font-normal text-muted">
                      · {group.kind === 'business_type' ? 'Business type' : 'Group'} · {group.clients} client
                      {group.clients === 1 ? '' : 's'}
                    </span>
                  </p>
                  {group.description ? <p className="text-xs text-muted">{group.description}</p> : null}
                </div>
                <Button size="sm" variant="secondary" onClick={() => setMembers(group)}>
                  <Users className="size-4" />
                  Clients
                </Button>
                {manage ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(group)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Delete ${group.name}`} onClick={() => setDeleting(group)}>
                      <Trash2 className="size-4" />
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
      {editing ? <GroupDialog group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {members ? <MembersDialog group={members} manage={manage} onClose={() => setMembers(null)} /> : null}
      <Confirm
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={`Delete ${deleting?.name ?? ''}?`}
        message="Requests in force that name it keep it until they are withdrawn — it cannot be deleted while any does."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={async () => {
          const target = deleting;
          setDeleting(null);
          if (target) await remove.mutateAsync(target.public_id).catch(() => undefined);
        }}
      />
    </Card>
  );
}

function GroupDialog({ group, onClose }: { group: Classification | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Classification['kind']>(group?.kind ?? 'business_type');
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const save = useMutation({
    mutationFn: () => requestsApi.saveClassification(group?.public_id ?? null, { kind, name, description }),
    onSuccess: async () => {
      toast.success('Saved');
      onClose();
      await queryClient.invalidateQueries({ queryKey: requestKeys.classifications });
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={group ? `Edit ${group.name}` : 'Add a business type or group'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {save.isError ? <Alert tone="danger">{errorText(save.error) ?? 'Not saved.'}</Alert> : null}
        <Select
          label="Kind"
          value={kind}
          options={[
            { value: 'business_type', label: 'Business type — what the business does' },
            { value: 'group', label: 'Group — any set of clients' },
          ]}
          onChange={(e) => setKind(e.target.value as Classification['kind'])}
        />
        <Input label="Name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        <Input label="What it is" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
      </div>
    </Modal>
  );
}

function MembersDialog({ group, manage, onClose }: { group: Classification; manage: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const members = useQuery({ queryKey: requestKeys.members(group.public_id), queryFn: () => requestsApi.members(group.public_id) });
  const [draft, setDraft] = useState<Named[] | null>(null);
  const shown = draft ?? members.data ?? [];
  const save = useMutation({
    mutationFn: () => requestsApi.setMembers(group.public_id, shown.map((m) => m.public_id)),
    onSuccess: async () => {
      toast.success('Saved', 'Requests that name it cover exactly these clients from now on.');
      onClose();
      await queryClient.invalidateQueries({ queryKey: requestKeys.all });
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Clients in ${group.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {manage ? 'Cancel' : 'Close'}
          </Button>
          {manage ? (
            <Button loading={save.isPending} disabled={draft === null} onClick={() => save.mutate()}>
              Save
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        {save.isError ? <Alert tone="danger">{errorText(save.error) ?? 'Not saved.'}</Alert> : null}
        {manage ? (
          <RecordSelect
            label="Add a client"
            endpoint="/crm/clients"
            value={null}
            onChange={(id, option) => {
              if (!id || shown.some((m) => m.public_id === id)) return;
              setDraft([...shown, { public_id: id, name: option?.label ?? 'A client' }]);
            }}
            toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
          />
        ) : null}
        {members.data === undefined ? (
          <QueryPending query={members} label="Loading" />
        ) : shown.length === 0 ? (
          <p className="text-sm text-muted">No clients in it.</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {shown.map((member) => (
              <li key={member.public_id} className="flex items-center justify-between py-2">
                {member.name}
                {manage ? (
                  <Checkbox
                    label="In it"
                    checked
                    onChange={() => setDraft(shown.filter((m) => m.public_id !== member.public_id))}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
